import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  passwordFormSchema,
  type ChangePasswordForm,
} from '@/lib/schemas/profile';
import { useChangePassword, useSetInitialPassword } from '@/lib/api/profile';
import { ApiError } from '@/lib/api';

/**
 * Раздел «Пароль» — current + new + confirm.
 *
 * Backend: `POST /profile/me/password`. min length 8 (Pydantic).
 * После смены пароль текущей сессии остаётся валиден; другие сессии можно
 * отозвать вручную в разделе «Активные сессии».
 *
 * Два режима. У приглашённого пользователя пароля ещё нет (`password_hash`
 * пустой), и поле «текущий пароль» превращало раздел в запертую дверь: ввести
 * туда нечего, а без него форма не сабмитится. Поэтому при
 * `hasPassword === false` поле «текущий» не показываем совсем и говорим
 * «Установить пароль», а не «Сменить».
 *
 * И уходим в ДРУГУЮ ручку. `POST /profile/me/password` хоть и пропускает
 * проверку старого пароля при пустом хеше, но его схема требует
 * `current_password` минимум в один символ — пустая строка отбивается 422 на
 * валидации, до всякой логики. Первый пароль ставит `POST /auth/set-password`
 * (та же ручка, что у экрана «Придумайте пароль» после приглашения), которому
 * старый пароль не нужен по определению. Поймано на CI: фронтовый тест
 * проверял тело запроса и потому молчал.
 */
export function ProfileSectionPassword({
  hasPassword = true,
}: {
  hasPassword?: boolean;
}) {
  const changeMut = useChangePassword();
  const setInitialMut = useSetInitialPassword();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(passwordFormSchema(hasPassword)),
    mode: 'onBlur',
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  const onSubmit = async (data: ChangePasswordForm) => {
    setServerError(null);
    try {
      if (hasPassword) {
        await changeMut.mutateAsync({
          current_password: data.current_password,
          new_password: data.new_password,
        });
      } else {
        await setInitialMut.mutateAsync({ password: data.new_password });
      }
      reset();
      setSuccess(true);
      window.setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      if (err instanceof ApiError) setServerError(err.detail);
      else setServerError('Сетевая ошибка');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      {serverError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{serverError}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            {hasPassword ? 'Пароль обновлён' : 'Пароль установлен'}
          </AlertDescription>
        </Alert>
      )}

      {hasPassword ? (
        <div className="space-y-1.5">
          <Label htmlFor="profile-current-password">Текущий пароль</Label>
          <PasswordInput
            id="profile-current-password"
            autoComplete="current-password"
            aria-invalid={!!errors.current_password}
            {...register('current_password')}
          />
          {errors.current_password && (
            <p className="text-destructive text-xs">
              {errors.current_password.message}
            </p>
          )}
        </div>
      ) : (
        <p className="text-muted-foreground text-xs">
          Вы вошли по приглашению — пароля у вас ещё нет. Придумайте его, чтобы
          входить обычным способом.
        </p>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="profile-new-password">
          {hasPassword ? 'Новый пароль' : 'Пароль'}
        </Label>
        <PasswordInput
          id="profile-new-password"
          autoComplete="new-password"
          aria-invalid={!!errors.new_password}
          {...register('new_password')}
        />
        {errors.new_password && (
          <p className="text-destructive text-xs">{errors.new_password.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="profile-confirm-password">
          {hasPassword ? 'Подтвердите новый пароль' : 'Повторите пароль'}
        </Label>
        <PasswordInput
          id="profile-confirm-password"
          autoComplete="new-password"
          aria-invalid={!!errors.confirm_password}
          {...register('confirm_password')}
        />
        {errors.confirm_password && (
          <p className="text-destructive text-xs">
            {errors.confirm_password.message}
          </p>
        )}
      </div>

      <Button
        type="submit"
        size="sm"
        disabled={isSubmitting}
        className="w-full"
      >
        {isSubmitting
          ? 'Сохраняем…'
          : hasPassword
            ? 'Сменить пароль'
            : 'Установить пароль'}
      </Button>
    </form>
  );
}
